import React, { useCallback, useMemo, useState, useRef } from 'react'
import cn from 'clsx'
import _uniqBy from 'lodash/uniqBy'
import _isUndefined from 'lodash/isUndefined'
import { useTranslation } from 'react-i18next'
import { type ListChildComponentProps, FixedSizeList } from 'react-window'

import GroupListItem from 'components/GroupListItem/GroupListItem'
import EmissionsListHeader from 'components/EmissionsListHeader/EmissionsFilterHeader'
import useEmissionsSelect from '../../hooks/use_emissions_select'
import isGroupSelected from './utils/is_group_selected'
import { type Emissions, type EmissionsGroup } from 'types/data'

import { CLASS_NAME } from './const'

import './style.scss'

type Props = {
  className?: string
  groups: EmissionsGroup[]
  selectedEmissions: Emissions[]
  setSelectedEmissions: (emissions: Emissions[]) => void
  itemWidthPX: number
  showSelectionIndex?: boolean
  subTitle?: string
  scrollWindowWidth?: number
  scrollWindowHeight?: number
}

export default function GroupList({
  groups,
  subTitle,
  className,
  itemWidthPX,
  scrollWindowWidth,
  selectedEmissions,
  scrollWindowHeight,
  setSelectedEmissions
}: Props) {
  const { t } = useTranslation()
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  )
  const [groupScrollOffsets, setGroupScrollOffsets] = useState<
    Record<string, number>
  >({})
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null)
  const emissions = useMemo(
    (): Emissions[] => groups.map(({ polygons }) => polygons).flat(),
    [groups]
  )

  const handleToggleExpand = (groupId: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId]
    }))
  }

  const handleGroupSelection = useCallback(
    (group: EmissionsGroup) => {
      const { polygons } = group
      const isSelected = isGroupSelected(selectedEmissions, group)

      // prettier-ignore
      const nextSelectedEmissions = isSelected
        ? selectedEmissions.filter(
          (emissions: Emissions) => (
            _isUndefined(polygons.find(({ id }) => id === emissions.id))
          )
        )
        : _uniqBy([...selectedEmissions, ...polygons], ({ id }) => id)

      setSelectedEmissions(nextSelectedEmissions)
    },
    [selectedEmissions, groups]
  )

  const {
    onSelectAll,
    onDeselectAll,
    isSelectAllDisabled,
    isDeselectAllDisabled
  } = useEmissionsSelect(emissions, selectedEmissions, setSelectedEmissions)

  const title = `${groups.length.toLocaleString()} ${t(
    'group_list.title_suffix'
  )}`

  const GroupListRow: React.FC<ListChildComponentProps> = ({
    index,
    style,
    data
  }: ListChildComponentProps): JSX.Element => {
    const group: EmissionsGroup = data[index]
    const finalStyle = {
      ...style,
      top: +(style?.top ?? 0) + 16,
      left: +(style?.left ?? 0) + 32,
      width: Math.max(0, (scrollWindowWidth ?? 0) - 64)
    }

    return (
      <li style={finalStyle}>
        <GroupListItem
          group={group}
          width={itemWidthPX}
          selectedEmissions={selectedEmissions}
          setSelectedEmissions={setSelectedEmissions}
          selected={isGroupSelected(selectedEmissions, group)}
          onClick={handleGroupSelection.bind(null, group)}
          isExpanded={Boolean(expandedGroups[group.key])}
          // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
          onToggleExpand={() => handleToggleExpand(group.key)}
          groupScrollOffset={groupScrollOffsets[group.key]}
          setGroupScrollOffset={(offset) => {
            if (offset === 0) return
            if (scrollTimeout.current != null) {
              clearTimeout(scrollTimeout.current)
            }
            scrollTimeout.current = setTimeout(() => {
              setGroupScrollOffsets((prev) => {
                if (prev[group.key] === offset) return prev
                return { ...prev, [group.key]: offset }
              })
            }, 100)
          }}
        />
      </li>
    )
  }

  return (
    <div className={cn(CLASS_NAME, className)}>
      <EmissionsListHeader
        title={title}
        subTitle={subTitle}
        onSelectAll={onSelectAll}
        onDeselectAll={onDeselectAll}
        isSelectAllEnabled={!isSelectAllDisabled}
        isDeselectAllEnabled={!isDeselectAllDisabled}
      />

      <ul className={`${CLASS_NAME}-inner`}>
        <FixedSizeList
          itemSize={252}
          itemData={groups}
          itemCount={groups.length}
          width={scrollWindowWidth ?? 0}
          height={Math.max(0, (scrollWindowHeight ?? 0) - 32)}
        >
          {GroupListRow}
        </FixedSizeList>
      </ul>
    </div>
  )
}

export { CLASS_NAME }
